import type { ResolvedSiteSection, Theme, Widget } from '@parkingbase/ecommerce-api-dev';

import { formatInGarageTz } from '@/lib/date-time-utils';
import { logger } from '@/lib/logger';
import { formatCurrencyAmount } from '@/lib/price-utils';
import { getCompanyBookingEnablement } from '@/lib/theme-utils';
import { whiteLabelTabHref } from '@/lib/white-label-nav';
import { BOOKING_ROUTES, PUBLIC_ROUTES, SITE_PAGE_PREFIX } from '@/routes/routes.constants';
import { AMENITY_LABEL_KEYS } from '@/app/(app)/reserve/reserve-utils';
import type {
  DirectoryGroupView,
  DirectoryLocationView,
  DirectoryView,
  EventItemView,
  EventsView,
  FeaturedAmenityView,
  FeaturedLocationView,
  FeaturedLocationsView,
  MonthlyPitchView,
  PromotionView,
  StatItemView,
  StatsView,
} from '@/components/site-page/SiteBoundViews.types';
import { ReservationMode } from '@/components/reserve/search-bar/SearchBar.types';
import type { ValuePropItem } from '@/components/landing/value-props';
import {
  SITE_ACTION_NAMES,
  SITE_ICON_NAMES,
  type AnnouncementView,
  type AuthoredHeroView,
  type FeatureCardView,
  type FeatureGridColumns,
  type FeatureGridView,
  type FooterBadgeView,
  type FooterLegalKind,
  type FooterLegalView,
  type FooterSocialView,
  type FooterView,
  type HeaderNavItemView,
  type HeaderView,
  type HeroCtaView,
  type HeroMedia,
  type QuickActionTileView,
  type RichTextView,
  type SearchModeName,
  type SearchView,
  type SiteAction,
  type SiteAudience,
  type SiteIcon,
  type SiteLinkView,
  type SiteSectionView,
  type SocialNetwork,
} from '@/components/site-page/SitePage.types';
import DOMPurify from 'isomorphic-dompurify';

/**
 * Pure mappers between the `GET /site/page` shapes and what the landing components render.
 * Raw API shapes stop here: blocks receive display-ready values only.
 */

const AUDIENCES: readonly SiteAudience[] = ['ANY', 'SIGNED_IN', 'SIGNED_OUT'];

/** The widget boxes, in the order the api's DEFAULT synthesis emits them. */
export type LegacyPosition = 'LEFT' | 'RIGHT' | 'TOP' | 'BOTTOM';
const LEGACY_POSITIONS: readonly LegacyPosition[] = ['LEFT', 'RIGHT', 'TOP', 'BOTTOM'];

/** A token longer than this is not one the api issued (payload `{c,p,exp,v}` + 32-byte HMAC). */
const MAX_PREVIEW_TOKEN_LENGTH = 2048;

const LOG_CONTEXT = { feature: 'site-page' } as const;

/** A non-blank string slot, or undefined (absent slot = the renderer's built-in default). */
export function stringSlot(slots: Record<string, unknown>, key: string): string | undefined {
  const value = slots[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Normalises API sections. A section with no id or type, or with an audience this client does
 * not know, is dropped with a warning — it can never be shown correctly. A missing audience
 * reads as `ANY`.
 */
export function normalizeSections(raw: ResolvedSiteSection[] | undefined): SiteSectionView[] {
  const result: SiteSectionView[] = [];
  for (const section of raw ?? []) {
    if (!section.id || !section.type) {
      logger.warn('site-page: dropped section without id or type', {
        ...LOG_CONTEXT,
        route: 'normalize',
      });
      continue;
    }
    const audience = (section.audience ?? 'ANY') as string;
    if (!AUDIENCES.includes(audience as SiteAudience)) {
      logger.warn('site-page: dropped section with unknown audience', {
        ...LOG_CONTEXT,
        route: 'normalize',
        type: section.type,
        audience,
      });
      continue;
    }
    const view: SiteSectionView = {
      id: section.id,
      type: section.type,
      audience: audience as SiteAudience,
      slots: section.slots ?? {},
    };
    // PB-8614: bound data is kept only when it is a plain object, and the key is otherwise left
    // off entirely, so a section without data normalises exactly as it did before.
    const data: unknown = section.data;
    if (isRecord(data)) view.data = data;
    result.push(view);
  }
  return result;
}

/** Reads a LEGACY_HTML section's position + html, or null when it has neither usable. */
export function readLegacyHtml(
  slots: Record<string, unknown>
): { position: LegacyPosition; html: string } | null {
  const position = stringSlot(slots, 'position')?.toUpperCase();
  const html = stringSlot(slots, 'html');
  if (!html || !LEGACY_POSITIONS.includes(position as LegacyPosition)) return null;
  return { position: position as LegacyPosition, html };
}

/**
 * Rebuilds the company `Widget` from the page's LEGACY_HTML sections.
 *
 * ## Invariants:
 * - HERO and `HeroSearch` decide hero copy, background media and the bottom panel from the whole
 *   widget (any non-empty position replaces the hero text; TOP starter markup keeps the media).
 *   Feeding them a widget rebuilt from these sections is what keeps the DEFAULT page identical
 *   to today's landing.
 * - Pass ONLY the sections the viewer can see (renderable and audience-allowed), never the raw
 *   API list: a LEGACY_HTML box hidden from this viewer must not change their hero (PB-8605 M1).
 */
export function buildWidgetFromSections(sections: SiteSectionView[]): Widget {
  const widgetMap: Record<string, string> = {};
  for (const section of sections) {
    if (section.type !== 'LEGACY_HTML') continue;
    const legacy = readLegacyHtml(section.slots);
    if (legacy) widgetMap[legacy.position] = legacy.html;
  }
  return { widgetMap };
}

/**
 * Client mirror of the api's `DefaultSitePageSynthesizer`: today's landing, in today's order.
 * Used when `GET /site/page` fails so the fallback goes through the same renderer.
 *
 * ## Invariants:
 * - Order and ids must match `DefaultSitePageSynthesizer.synthesize`: widget LEFT, RIGHT, TOP,
 *   HERO, widget BOTTOM, PERSONAL_PANEL, SIGNUP_CTA, TESTIMONIALS, FEATURE_GRID.
 */
export function synthesizeDefaultSections(widget: Widget | null | undefined): SiteSectionView[] {
  const boxes = widget?.widgetMap ?? {};
  const legacy = (position: LegacyPosition): SiteSectionView[] => {
    const html = boxes[position];
    if (!html || !html.trim()) return [];
    return [
      {
        id: `default-legacy-${position.toLowerCase()}`,
        type: 'LEGACY_HTML',
        audience: 'ANY',
        slots: { position, html },
      },
    ];
  };
  return [
    ...legacy('LEFT'),
    ...legacy('RIGHT'),
    ...legacy('TOP'),
    {
      id: 'default-hero',
      type: 'HERO',
      audience: 'ANY',
      slots: { media: 'THEME', showSearch: true },
    },
    ...legacy('BOTTOM'),
    {
      id: 'default-personal-panel',
      type: 'PERSONAL_PANEL',
      audience: 'SIGNED_IN',
      slots: { show: true },
    },
    { id: 'default-signup-cta', type: 'SIGNUP_CTA', audience: 'SIGNED_OUT', slots: {} },
    {
      id: 'default-testimonials',
      type: 'TESTIMONIALS',
      audience: 'ANY',
      slots: { source: 'TRUSTPILOT' },
    },
    { id: 'default-feature-grid', type: 'FEATURE_GRID', audience: 'ANY', slots: {} },
  ];
}

/** HERO text slots; absent → undefined so `HeroSearch` keeps today's i18n copy. */
export function mapHeroSlots(slots: Record<string, unknown>): {
  headline?: string;
  subheadline?: string;
} {
  return { headline: stringSlot(slots, 'headline'), subheadline: stringSlot(slots, 'subheadline') };
}

/** SIGNUP_CTA text slots; absent → undefined so `SignupCta` keeps today's copy. */
export function mapSignupCtaSlots(slots: Record<string, unknown>): {
  heading?: string;
  ctaLabel?: string;
} {
  return { heading: stringSlot(slots, 'heading'), ctaLabel: stringSlot(slots, 'ctaLabel') };
}

/** TESTIMONIALS source slot (`TRUSTPILOT` | `MANUAL`); undefined when absent. */
export function getTestimonialsSource(slots: Record<string, unknown>): string | undefined {
  return stringSlot(slots, 'source');
}

/** FEATURE_GRID row layout = no heading and no intro. With either, it is Child E's card grid. */
export function isFeatureGridRow(slots: Record<string, unknown>): boolean {
  return !stringSlot(slots, 'heading') && !stringSlot(slots, 'intro');
}

/**
 * FEATURE_GRID row tiles from the `cards` slot (api contract: `{icon, title, blurb, link}`).
 * Returns undefined when there are no usable cards, so `ValueProps` renders today's three tiles.
 * Icon and link are ignored in the row layout.
 */
export function mapFeatureGridItems(slots: Record<string, unknown>): ValuePropItem[] | undefined {
  const cards = slots.cards;
  if (!Array.isArray(cards)) return undefined;
  const items: ValuePropItem[] = [];
  for (const card of cards) {
    if (typeof card !== 'object' || card === null) continue;
    const record = card as Record<string, unknown>;
    const title = stringSlot(record, 'title');
    if (!title) continue;
    items.push({ title, subtitle: stringSlot(record, 'blurb') });
  }
  return items.length > 0 ? items : undefined;
}

/**
 * Whether this client can render the section. A headed FEATURE_GRID with no usable card and a
 * RICH_TEXT with no body have nothing to show, so they are treated exactly like an unknown type
 * (no wrapper, one warning per type).
 *
 * ## Invariants:
 * - TESTIMONIALS: TRUSTPILOT is supported as before; MANUAL only when at least one valid item
 *   exists (an empty MANUAL list would render an empty heading); a missing or unknown source is
 *   unsupported (PB-8615).
 */
export function isSectionSupported(section: SiteSectionView): boolean {
  if (section.type === 'TESTIMONIALS') {
    const source = getTestimonialsSource(section.slots);
    if (source === 'TRUSTPILOT') return true;
    if (source === 'MANUAL') return mapManualTestimonials(section.slots).length > 0;
    return false;
  }
  if (section.type === 'FEATURE_GRID') {
    return isFeatureGridRow(section.slots) || mapFeatureGridView(section.slots).cards.length > 0;
  }
  if (section.type === 'RICH_TEXT') return mapRichTextSlots(section.slots) !== null;
  return true;
}

/** The current page URL with `previewToken` removed (path, other query and hash kept). */
export function buildExitPreviewUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.delete('previewToken');
  return url.toString();
}

/**
 * Decodes the `exp` claim (epoch seconds) from a preview token for DISPLAY ONLY.
 *
 * Token shape (api `SitePreviewTokenService`): `base64url(payloadJson).base64url(hmac)`, payload
 * `{c,p,exp,v}`. The signature is not verified here: the server already validated the token, as
 * the response being `source: PREVIEW` proves. Never use the result for authorization.
 *
 * ## Invariants:
 * - The input comes from the URL, so it is bounded (over 2 KB returns null before any work) and
 *   parsed with one `split` and no regular expression.
 * - Every failure returns null AND warns; the caller then shows the banner without a time.
 *
 * @returns the expiry instant, or null when the token cannot be decoded
 */
export function decodePreviewTokenExp(token: string): Date | null {
  const fail = (reason: string): null => {
    logger.warn('site-page: preview token expiry could not be decoded', {
      ...LOG_CONTEXT,
      route: 'preview-token',
      reason,
    });
    return null;
  };
  if (token.length > MAX_PREVIEW_TOKEN_LENGTH) return fail('too-long');
  const parts = token.split('.');
  if (parts.length !== 2 || !parts[0]) return fail('shape');
  try {
    const base64 = parts[0].split('-').join('+').split('_').join('/');
    const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
    const exp = (claims as { exp?: unknown } | null)?.exp;
    if (typeof exp !== 'number' || !Number.isFinite(exp) || exp <= 0) return fail('exp');
    return new Date(exp * 1000);
  } catch {
    return fail('parse');
  }
}

// ---------------------------------------------------------------------------
// PB-8609 (Child E): slot mappers for the authored blocks.
//
// Everything below turns an untyped `slots` bag into a display-ready view. Blocks never read raw
// slots: a value that is not what the contract says is dropped here, once, so every block treats
// operator text as text and every link as already-safe.
// ---------------------------------------------------------------------------

/** The api caps a LINK value at this length (`SiteLinkValidator.MAX_LINK_LENGTH`). */
const MAX_LINK_LENGTH = 2048;

const ICON_NAMES: ReadonlySet<string> = new Set(SITE_ICON_NAMES);
const ACTION_NAMES: ReadonlySet<string> = new Set(SITE_ACTION_NAMES);

/** Type guard for a plain slot object. Arrays are not records. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A boolean slot with an explicit default for "absent or not a boolean". */
export function boolSlot(slots: Record<string, unknown>, key: string, fallback: boolean): boolean {
  const value = slots[key];
  return typeof value === 'boolean' ? value : fallback;
}

/** Linear scan: any whitespace or control character (browsers strip some, which re-routes a URL). */
function hasControlOrSpace(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * An in-app path: starts with a single `/`, no backslash, no whitespace or control character.
 * `//host` and `/\host` are read by browsers as protocol-relative, so they are rejected.
 */
function isSafeRoute(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (path.includes('\\')) return false;
  return !hasControlOrSpace(path);
}

/** True for `https://host...` with a host and no embedded credentials. */
export function isHttpsUrl(value: string): boolean {
  if (value.length === 0 || value.length > MAX_LINK_LENGTH) return false;
  if (value.slice(0, 8).toLowerCase() !== 'https://') return false;
  // `https:///x` parses (WHATWG) with `x` as the host; the api's own check rejects it, so do we.
  if (value.charAt(8) === '/' || value.charAt(8) === '\\') return false;
  if (hasControlOrSpace(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname !== '' && !url.username && !url.password;
  } catch (error) {
    logger.warn('site-page: unparseable https url slot', {
      ...LOG_CONTEXT,
      route: 'link',
      error: error instanceof Error ? error.message : 'unknown',
    });
    return false;
  }
}

/**
 * Resolves a LINK slot (`{type: ROUTE | WHITE_LABEL_TAB | URL, value}`) to a destination that is
 * safe to put in an `href`, or null.
 *
 * ## Invariants:
 * - This is the ONE link resolver; every LINK slot of every block goes through it. A null result
 *   means "render the label as plain text, no anchor" — never a fallback href.
 * - ROUTE accepts only `/…` (not `//`, not `/\`, no whitespace). WHITE_LABEL_TAB resolves through
 *   `whiteLabelTabHref`, the function the existing white-label tabs use. URL accepts only
 *   `https://` with a host. `javascript:`, `data:`, `http:`, protocol-relative and
 *   leading-whitespace values all return null.
 * - PAGE (PB-9031) resolves ONLY through the api-stamped `path`: it must be a string, pass the
 *   same safe-route check, and be exactly `/` or `/pages/…`. `value` (the page key) is never an
 *   href. A `path` that is `null` (target unpublished at read time) or ABSENT (a document read
 *   before the api stamped paths) returns null. Why: an unpublished target must not render a
 *   dead link, and an absent path must never become `href="undefined"`.
 * - Linear time: `startsWith`, a character loop, `split`, and one `new URL()`; no backtracking
 *   regex.
 */
export function resolveSiteLink(raw: unknown): SiteLinkView | null {
  if (!isRecord(raw)) return null;
  const { type, value } = raw;
  if (typeof type !== 'string' || typeof value !== 'string') return null;
  if (value.length === 0 || value.length > MAX_LINK_LENGTH) return null;
  switch (type) {
    case 'ROUTE':
      return isSafeRoute(value) ? { href: value, external: false } : null;
    case 'WHITE_LABEL_TAB': {
      const href = whiteLabelTabHref(value);
      return href !== null && isSafeRoute(href) ? { href, external: false } : null;
    }
    case 'URL':
      return isHttpsUrl(value) ? { href: value, external: true } : null;
    case 'PAGE':
      return resolvePagePath(raw.path);
    default:
      return null;
  }
}

/**
 * The href of a PAGE link from the api-stamped `path`, or null. `/` (the homepage) or a
 * `/pages/…` path that is a safe route with no dot segment; anything else is rejected.
 */
function resolvePagePath(path: unknown): SiteLinkView | null {
  if (typeof path !== 'string' || path.length === 0 || path.length > MAX_LINK_LENGTH) return null;
  if (!isSafeRoute(path)) return null;
  if (path !== '/' && !path.startsWith(`${SITE_PAGE_PREFIX}/`)) return null;
  if (path.split('/').some((segment) => segment === '.' || segment === '..')) return null;
  return { href: path, external: false };
}

/**
 * The href of a navigation PAGE target or Overview path (`/` or a safe `/pages/…` path), or null.
 * The same check a PAGE link slot goes through, exported for the site header (PB-9032).
 */
export function resolveSitePagePath(path: unknown): SiteLinkView | null {
  return resolvePagePath(path);
}

/**
 * True for a PAGE link that does not resolve: its target is unpublished (`path: null`), or the
 * api did not stamp a path. Such a link is NOT shown (a button, tile or nav item is dropped).
 * A non-PAGE link, a resolvable PAGE link or a non-link value is false.
 */
export function isUnpublishedPageLink(raw: unknown): boolean {
  return isRecord(raw) && raw.type === 'PAGE' && resolveSiteLink(raw) === null;
}

/** A LINK slot that must be an absolute https URL (social profiles). Null otherwise. */
function resolveHttpsLink(raw: unknown): SiteLinkView | null {
  const link = resolveSiteLink(raw);
  return link?.external ? link : null;
}

/** An IMAGE slot: an https URL string, else undefined. */
function httpsImageSlot(slots: Record<string, unknown>, key: string): string | undefined {
  const value = stringSlot(slots, key);
  return value !== undefined && isHttpsUrl(value) ? value : undefined;
}

function isSiteIcon(value: unknown): value is SiteIcon {
  return typeof value === 'string' && ICON_NAMES.has(value);
}

function isSiteAction(value: unknown): value is SiteAction {
  return typeof value === 'string' && ACTION_NAMES.has(value);
}

/** The items of a LIST slot that are objects; anything else is dropped. */
function recordItems(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  const items: Record<string, unknown>[] = [];
  for (const item of value) {
    if (isRecord(item)) items.push(item);
  }
  return items;
}

/**
 * ANNOUNCEMENT slots; null when there is no text (nothing to show).
 *
 * ## Invariants:
 * - Every omitted BOOLEAN / ENUM / NUMBER slot takes its api `SiteBlockSchemaRegistry` default,
 *   because the resolver returns slots as authored and never fills defaults. This holds for every
 *   mapper below: dismissible=true, showLogin=true, hiddenSignedOut=false, media=THEME,
 *   overlay=false, showSearch=true (and PERSONAL_PANEL show=true in its block).
 */
export function mapAnnouncementSlots(slots: Record<string, unknown>): AnnouncementView | null {
  const text = stringSlot(slots, 'text');
  if (!text) return null;
  return {
    text,
    link: resolveSiteLink(slots.link),
    // Contract default (SiteBlockSchemaRegistry `bool("dismissible", true)`): the api does not
    // fill defaults on resolve, so an omitted slot must read as dismissible here.
    dismissible: boolSlot(slots, 'dismissible', true),
  };
}

/**
 * HEADER slots. An item with no label, or one that targets an unpublished page, is dropped; one
 * with any other bad target keeps its label.
 */
export function mapHeaderSlots(slots: Record<string, unknown>): HeaderView {
  const navItems: HeaderNavItemView[] = [];
  for (const item of recordItems(slots.navItems)) {
    const label = stringSlot(item, 'label');
    if (!label || isUnpublishedPageLink(item.target)) continue;
    navItems.push({
      label,
      link: resolveSiteLink(item.target),
      hiddenSignedOut: item.hiddenSignedOut === true,
    });
  }
  return { navItems, showLogin: boolSlot(slots, 'showLogin', true) };
}

function mapMedia(slots: Record<string, unknown>): HeroMedia {
  const media = slots.media;
  return media === 'IMAGE' || media === 'VIDEO' || media === 'NONE' ? media : 'THEME';
}

/**
 * A CTA / tile target: the action, plus the resolved `url` link when the action is URL. Null when
 * the action is unknown or the URL target is an unpublished PAGE (the caller drops the CTA/tile).
 */
function mapActionTarget(
  raw: Record<string, unknown>
): { action: SiteAction; link: SiteLinkView | null } | null {
  const action = raw.action;
  if (!isSiteAction(action)) return null;
  // PB-9031: a button or tile that targets an unpublished page is not shown at all.
  if (action === 'URL' && isUnpublishedPageLink(raw.url)) return null;
  return { action, link: action === 'URL' ? resolveSiteLink(raw.url) : null };
}

function mapCta(raw: unknown): HeroCtaView | null {
  if (!isRecord(raw)) return null;
  const label = stringSlot(raw, 'label');
  const target = mapActionTarget(raw);
  if (!label || !target) return null;
  return { label, ...target };
}

/** HERO slots as the authored hero reads them. Absent slots take the contract defaults. */
export function mapAuthoredHeroSlots(slots: Record<string, unknown>): AuthoredHeroView {
  const rotatingWords: string[] = [];
  if (Array.isArray(slots.rotatingWords)) {
    for (const word of slots.rotatingWords) {
      if (typeof word === 'string' && word.trim()) rotatingWords.push(word);
    }
  }
  return {
    headline: stringSlot(slots, 'headline'),
    subheadline: stringSlot(slots, 'subheadline'),
    rotatingWords,
    media: mapMedia(slots),
    imageUrl: httpsImageSlot(slots, 'imageUrl'),
    imageAlt: stringSlot(slots, 'imageAlt'),
    overlay: slots.overlay === true,
    primaryCta: mapCta(slots.primaryCta),
    secondaryCta: mapCta(slots.secondaryCta),
    showSearch: boolSlot(slots, 'showSearch', true),
  };
}

/**
 * Whether a HERO uses any slot beyond headline/subheadline.
 *
 * ## Invariants:
 * - The DEFAULT / synthesized hero (`{ media: 'THEME', showSearch: true }`, optionally with
 *   headline/subheadline) is NOT authored, so it keeps rendering through `HeroSearch` exactly as
 *   before this ticket.
 */
export function isAuthoredHero(slots: Record<string, unknown>): boolean {
  const hero = mapAuthoredHeroSlots(slots);
  return (
    hero.rotatingWords.length > 0 ||
    hero.primaryCta !== null ||
    hero.secondaryCta !== null ||
    hero.media === 'IMAGE' ||
    hero.media === 'NONE' ||
    !hero.showSearch
  );
}

/** Whether the HERO embeds the search card (every non-authored hero does). */
export function heroShowsSearch(slots: Record<string, unknown>): boolean {
  return !isAuthoredHero(slots) || mapAuthoredHeroSlots(slots).showSearch;
}

const SEARCH_MODE_NAMES: readonly SearchModeName[] = ['HOURLY', 'MONTHLY'];

function toSearchModeName(value: unknown): SearchModeName | undefined {
  return SEARCH_MODE_NAMES.find((name) => name === value);
}

/** SEARCH slots. Only HOURLY and MONTHLY exist in the portal; DAILY / EVENT are dropped. */
export function mapSearchSlots(slots: Record<string, unknown>): SearchView {
  const modes: SearchModeName[] = [];
  if (Array.isArray(slots.modes)) {
    for (const raw of slots.modes) {
      const mode = toSearchModeName(raw);
      if (mode && !modes.includes(mode)) modes.push(mode);
    }
  }
  const defaultMode = toSearchModeName(slots.defaultMode);
  const modeLabels: SearchView['modeLabels'] = {};
  if (isRecord(slots.modeLabels)) {
    for (const mode of SEARCH_MODE_NAMES) {
      const label = stringSlot(slots.modeLabels, mode);
      if (label) modeLabels[mode] = label;
    }
  }
  return {
    placeholder: stringSlot(slots, 'placeholder'),
    modes,
    defaultMode: defaultMode && modes.includes(defaultMode) ? defaultMode : undefined,
    geolocate: boolSlot(slots, 'geolocate', true),
    modeLabels,
  };
}

/** Slot mode name → the portal's `ReservationMode`. */
export function toReservationMode(mode: SearchModeName): ReservationMode {
  return mode === 'MONTHLY' ? ReservationMode.monthly : ReservationMode.hourly;
}

/** The modes the company has switched on, hourly first (`getCompanyBookingEnablement`). */
function companyEnabledModes(theme: Theme | null | undefined): ReservationMode[] {
  const enablement = getCompanyBookingEnablement(theme);
  return [ReservationMode.hourly, ReservationMode.monthly].filter((mode) =>
    mode === ReservationMode.monthly ? enablement.monthly : enablement.hourly
  );
}

/**
 * The modes a SEARCH block's card offers.
 *
 * ## Invariants:
 * - Slot modes ∩ company enablement. An empty intersection falls back to the company-enabled
 *   modes, so the card is never mode-less.
 */
export function searchBlockAllowedModes(
  theme: Theme | null | undefined,
  modes: readonly SearchModeName[]
): ReservationMode[] {
  const companyModes = companyEnabledModes(theme);
  const slotModes = modes.map(toReservationMode).filter((mode) => companyModes.includes(mode));
  return slotModes.length > 0 ? slotModes : companyModes;
}

/**
 * The modes the page's search panel can be put in; empty when the page has no search panel.
 *
 * ## Invariants:
 * - The panel is the first SEARCH block when there is one (the hero then embeds no card), so its
 *   modes are `searchBlockAllowedModes`. Otherwise it is the card a HERO embeds, which offers every
 *   company-enabled mode.
 * - BOOK / MONTHLY read this, so a quick action or CTA never switches the panel into a mode the
 *   panel does not offer (R2-F3); a mode outside this list goes to `/reserve` instead.
 */
export function pageSearchModes(
  sections: readonly SiteSectionView[],
  theme: Theme | null | undefined
): ReservationMode[] {
  const search = sections.find((section) => section.type === 'SEARCH');
  if (search) return searchBlockAllowedModes(theme, mapSearchSlots(search.slots).modes);
  const heroCard = sections.some(
    (section) => section.type === 'HERO' && heroShowsSearch(section.slots)
  );
  return heroCard ? companyEnabledModes(theme) : [];
}

/** QUICK_ACTIONS tiles. A tile with no label or an unknown action cannot be shown correctly. */
export function mapQuickActionTiles(slots: Record<string, unknown>): QuickActionTileView[] {
  const tiles: QuickActionTileView[] = [];
  for (const item of recordItems(slots.tiles)) {
    const label = stringSlot(item, 'label');
    const target = mapActionTarget(item);
    if (!label || !target) continue;
    tiles.push({ label, icon: isSiteIcon(item.icon) ? item.icon : null, ...target });
  }
  return tiles;
}

function toColumns(value: unknown): FeatureGridColumns {
  return value === 4 || value === 6 ? value : 3;
}

/** FEATURE_GRID (card layout). A card with no title is dropped. */
export function mapFeatureGridView(slots: Record<string, unknown>): FeatureGridView {
  const cards: FeatureCardView[] = [];
  for (const item of recordItems(slots.cards)) {
    const title = stringSlot(item, 'title');
    if (!title) continue;
    cards.push({
      icon: isSiteIcon(item.icon) ? item.icon : null,
      title,
      blurb: stringSlot(item, 'blurb'),
      link: resolveSiteLink(item.link),
    });
  }
  return {
    heading: stringSlot(slots, 'heading'),
    intro: stringSlot(slots, 'intro'),
    columns: toColumns(slots.columns),
    cards,
  };
}

/** RICH_TEXT slots; null without a body. The body is raw HTML: sanitize before rendering it. */
export function mapRichTextSlots(slots: Record<string, unknown>): RichTextView | null {
  const body = stringSlot(slots, 'body');
  if (!body) return null;
  return { heading: stringSlot(slots, 'heading'), body };
}

const SOCIAL_NETWORKS: readonly SocialNetwork[] = [
  'facebook',
  'instagram',
  'linkedin',
  'x',
  'youtube',
  'tiktok',
];

const LEGAL_KINDS: readonly FooterLegalKind[] = [
  'PRIVACY',
  'TERMS',
  'ACCESSIBILITY',
  'ALPR_NOTICE',
];

function toLegalKind(value: unknown): FooterLegalKind | undefined {
  return LEGAL_KINDS.find((kind) => kind === value);
}

/** FOOTER slots. Empty columns stay empty so the block can omit them. */
export function mapFooterSlots(slots: Record<string, unknown>): FooterView {
  const contact = isRecord(slots.contact) ? slots.contact : {};
  const socialUrls = isRecord(slots.socialUrls) ? slots.socialUrls : {};

  const socials: FooterSocialView[] = [];
  for (const network of SOCIAL_NETWORKS) {
    const link = resolveHttpsLink(socialUrls[network]);
    if (link) socials.push({ network, href: link.href });
  }

  const badges: FooterBadgeView[] = [];
  for (const item of recordItems(slots.badges)) {
    const imageUrl = httpsImageSlot(item, 'imageUrl');
    if (!imageUrl) continue;
    badges.push({
      imageUrl,
      alt: stringSlot(item, 'alt') ?? '',
      link: resolveSiteLink(item.link),
    });
  }

  const legal: FooterLegalView[] = [];
  for (const item of recordItems(slots.legalLinks)) {
    const kind = toLegalKind(item.kind);
    if (!kind || legal.some((entry) => entry.kind === kind)) continue;
    const link = resolveSiteLink(item.link);
    legal.push({
      kind,
      label: stringSlot(item, 'label'),
      link,
      // An unpublished PAGE target reads as "no link authored", so `composeFooterLegal` falls back
      // to the route default and Privacy / Terms / Accessibility stay reachable (PB-9031).
      linkRejected: item.link !== undefined && link === null && !isUnpublishedPageLink(item.link),
    });
  }

  return {
    address: stringSlot(contact, 'address'),
    phone: stringSlot(contact, 'phone'),
    email: stringSlot(contact, 'email'),
    hours: stringSlot(contact, 'hours'),
    socials,
    badges,
    legal,
  };
}

/** One legal link as the footer renders it. `label` undefined = use the i18n default. */
export interface FooterLegalLink {
  kind: FooterLegalKind;
  label?: string;
  link: SiteLinkView | null;
}

const REQUIRED_LEGAL: ReadonlyArray<{ kind: FooterLegalKind; route: string }> = [
  { kind: 'PRIVACY', route: PUBLIC_ROUTES.privacy },
  { kind: 'TERMS', route: PUBLIC_ROUTES.terms },
  { kind: 'ACCESSIBILITY', route: PUBLIC_ROUTES.accessibility },
];

/**
 * The footer's legal links.
 *
 * ## Invariants:
 * - Privacy, Terms and Accessibility are ALWAYS present, in that order, even when the slots omit
 *   them; the operator may reword the label or retarget the link but not remove them. Without an
 *   operator entry the link is the `PUBLIC_ROUTES` path. An entry whose link failed
 *   `resolveSiteLink` renders as plain text (no fallback href).
 * - ALPR_NOTICE renders only when the slots include it (the api does not yet filter it by LPR
 *   use; a follow-up drops it server-side for companies without LPR).
 */
export function composeFooterLegal(legal: FooterLegalView[]): FooterLegalLink[] {
  const result: FooterLegalLink[] = REQUIRED_LEGAL.map(({ kind, route }) => {
    const entry = legal.find((candidate) => candidate.kind === kind);
    if (!entry) return { kind, link: { href: route, external: false } };
    return {
      kind,
      label: entry.label,
      link: entry.linkRejected ? null : (entry.link ?? { href: route, external: false }),
    };
  });
  const alpr = legal.find((candidate) => candidate.kind === 'ALPR_NOTICE');
  if (alpr) result.push({ kind: 'ALPR_NOTICE', label: alpr.label, link: alpr.link });
  return result;
}

/**
 * A `tel:` href for an operator-typed phone number, or null when it has no digits.
 * Only digits and a leading-style `+` survive, so the operator text can never add a second
 * scheme, a parameter or markup to the href.
 */
export function telHref(phone: string): string | null {
  let digits = '';
  for (const char of phone) {
    if ((char >= '0' && char <= '9') || char === '+') digits += char;
  }
  return digits.replace(/\+/g, '').length > 0 ? `tel:${digits}` : null;
}

/**
 * A `mailto:` href for an operator-typed address, or null when it is not `local@domain`.
 * Both halves are percent-encoded, so `?`, `&` and `,` cannot add headers or recipients.
 */
export function mailtoHref(email: string): string | null {
  if (hasControlOrSpace(email)) return null;
  const parts = email.split('@');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return `mailto:${encodeURIComponent(parts[0])}@${encodeURIComponent(parts[1])}`;
}

// ── PB-8615 growth blocks ────────────────────────────────────────────────────────────────────

/** One MANUAL testimonial card. */
export interface TestimonialItem {
  quote: string;
  name: string;
  role?: string;
  /** Whole number 1-5, or undefined when absent/invalid (no stars are drawn). */
  stars?: number;
}

/** MANUAL testimonial cards; an item without a quote or a name is dropped. */
export function mapManualTestimonials(slots: Record<string, unknown>): TestimonialItem[] {
  const items: TestimonialItem[] = [];
  for (const record of recordItems(slots.items)) {
    const quote = stringSlot(record, 'quote');
    const name = stringSlot(record, 'name');
    if (!quote || !name) continue;
    const stars = record.stars;
    items.push({
      quote,
      name,
      role: stringSlot(record, 'role'),
      stars:
        typeof stars === 'number' && Number.isInteger(stars) && stars >= 1 && stars <= 5
          ? stars
          : undefined,
    });
  }
  return items;
}

/** An image source the storefront will load: https, http or a site-relative path. */
function toImageSrc(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const trimmed = value.trim();
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;
  try {
    const { protocol } = new URL(trimmed);
    return protocol === 'https:' || protocol === 'http:' ? trimmed : undefined;
  } catch {
    return undefined;
  }
}

export interface LogoWallLogo {
  imageUrl: string;
  alt: string;
  /** Resolved link target; undefined renders a static, non-focusable image. */
  href?: string;
}

export interface LogoWallView {
  caption?: string;
  logos: LogoWallLogo[];
}

/**
 * LOGO_WALL slots. A logo without an image or without alt text is dropped: alt is required for
 * every image (AC), so an un-described logo is never shown. No logos left -> null (section absent).
 */
export function mapLogoWall(slots: Record<string, unknown>): LogoWallView | null {
  const logos: LogoWallLogo[] = [];
  for (const record of recordItems(slots.logos)) {
    const imageUrl = toImageSrc(record.imageUrl);
    const alt = stringSlot(record, 'alt');
    if (!imageUrl || !alt) continue;
    logos.push({ imageUrl, alt, href: resolveSiteLink(record.link)?.href });
  }
  return logos.length > 0 ? { caption: stringSlot(slots, 'caption'), logos } : null;
}

export interface HowItWorksStep {
  icon?: SiteIcon;
  title: string;
  text: string;
}

/** HOW_IT_WORKS steps; a step without a title or text is dropped, none left -> null. */
export function mapHowItWorks(slots: Record<string, unknown>): HowItWorksStep[] | null {
  const steps: HowItWorksStep[] = [];
  for (const record of recordItems(slots.steps)) {
    const title = stringSlot(record, 'title');
    const text = stringSlot(record, 'text');
    if (!title || !text) continue;
    steps.push({ title, text, icon: isSiteIcon(record.icon) ? record.icon : undefined });
  }
  return steps.length > 0 ? steps : null;
}

/**
 * A store URL from a LINK slot. Only `{type: 'URL', value: 'https://...'}` is accepted.
 *
 * ## Invariants:
 * - Anything else (ROUTE links, `http:`, `javascript:`, malformed) returns undefined, so the badge
 *   is not rendered rather than rendered with an unsafe or dead href.
 */
export function resolveStoreUrl(link: unknown): string | undefined {
  if (!isRecord(link) || link.type !== 'URL') return undefined;
  return resolveSiteLink(link)?.href;
}

export interface AppDownloadView {
  heading?: string;
  body?: string;
  appStoreUrl?: string;
  playStoreUrl?: string;
}

/** APP_DOWNLOAD slots; with neither store URL valid there is nothing to link to -> null. */
export function mapAppDownload(slots: Record<string, unknown>): AppDownloadView | null {
  const appStoreUrl = resolveStoreUrl(slots.appStoreUrl);
  const playStoreUrl = resolveStoreUrl(slots.playStoreUrl);
  if (!appStoreUrl && !playStoreUrl) return null;
  return {
    heading: stringSlot(slots, 'heading'),
    body: stringSlot(slots, 'body'),
    appStoreUrl,
    playStoreUrl,
  };
}

export interface FaqItem {
  question: string;
  /** Authored HTML; sanitised again at render (`HtmlContent`) and reduced to text for JSON-LD. */
  answerHtml: string;
}

/** FAQ items; an item without a question or with an answer that has no visible text is dropped. */
export function mapFaq(slots: Record<string, unknown>): FaqItem[] | null {
  const items: FaqItem[] = [];
  for (const record of recordItems(slots.items)) {
    const question = stringSlot(record, 'question');
    const answerHtml = stringSlot(record, 'answer');
    if (!question || !answerHtml || htmlToPlainText(answerHtml) === '') continue;
    items.push({ question, answerHtml });
  }
  return items.length > 0 ? items : null;
}

const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'BR',
  'LI',
  'UL',
  'OL',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'TR',
]);

function collectText(node: Node, out: string[]): void {
  if (node.nodeType === 3) {
    out.push(node.textContent ?? '');
    return;
  }
  const isBlock = node.nodeType === 1 && BLOCK_TAGS.has((node as Element).tagName);
  if (isBlock) out.push(' ');
  node.childNodes.forEach((child) => collectText(child, out));
  if (isBlock) out.push(' ');
}

/**
 * Authored HTML -> plain text. Sanitised with DOMPurify, then read through the DOM
 * (`textContent`): no regular expression touches the markup, so there is no pattern to
 * backtrack on. Block boundaries become one space; runs of whitespace collapse.
 */
export function htmlToPlainText(html: string): string {
  const body = DOMPurify.sanitize(html, { RETURN_DOM: true }) as HTMLElement;
  const parts: string[] = [];
  collectText(body, parts);
  return parts.join('').split(/\s+/).filter(Boolean).join(' ');
}

/** The schema.org FAQPage object for the page's FAQ items. */
export function buildFaqJsonLd(items: FaqItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: htmlToPlainText(item.answerHtml) },
    })),
  };
}

/**
 * Serialises JSON-LD for an inline `<script type="application/ld+json">`.
 *
 * ## Invariants:
 * - Every `<` becomes `\u003c`. Why: authored text can contain `</script>`, which would end the
 *   script element early and let the rest run as markup. `\u003c` is the same character to a JSON
 *   parser, so the structured data is unchanged.
 */
export function serializeJsonLd(value: unknown): string {
  return JSON.stringify(value).split('<').join('\\u003c');
}

/** Lead-form field keys, in the api's declaration order. */
export const LEAD_FIELD_KEYS = [
  'NAME',
  'EMAIL',
  'PHONE',
  'COMPANY',
  'PROPERTY_ADDRESS',
  'PROPERTY_TYPE',
  'STALL_COUNT',
  'MESSAGE',
] as const;
export type LeadFieldKey = (typeof LEAD_FIELD_KEYS)[number];

export interface LeadFormView {
  heading?: string;
  /** Configured fields in slot order, de-duplicated, unknown keys dropped. */
  fields: LeadFieldKey[];
  nextSteps: string[];
}

/**
 * LEAD_FORM slots. Fields render in the order the operator configured them.
 *
 * ## Invariants:
 * - `recipientEmail` is never read, mapped or returned. Why: it is the operator's inbox; the
 *   public page response may carry it (follow-up api bug) but the storefront must not surface it.
 */
export function mapLeadForm(slots: Record<string, unknown>): LeadFormView | null {
  const raw = Array.isArray(slots.fields) ? slots.fields : [];
  const fields: LeadFieldKey[] = [];
  for (const key of raw) {
    if (typeof key !== 'string') continue;
    const known = LEAD_FIELD_KEYS.find((k) => k === key);
    if (known && !fields.includes(known)) fields.push(known);
  }
  if (fields.length === 0) return null;
  const nextSteps = recordItems(slots.nextSteps)
    .map((step) => stringSlot(step, 'title'))
    .filter((title): title is string => !!title);
  return { heading: stringSlot(slots, 'heading'), fields, nextSteps };
}

// ── PB-8614 bound blocks (Child K) ───────────────────────────────────────────────────────────
//
// DIRECTORY, FEATURED_LOCATIONS, MONTHLY_PITCH, EVENTS, STATS and PROMOTION render values the
// api computed into `section.data` (PB-8607). The mappers below are the ONLY code that reads
// `data`; blocks receive the display-ready views from `SiteBoundViews.types.ts`.

/** True when `data` is a plain object whose `status` is `OK` (EMPTY / UNAVAILABLE / absent: false). */
export function boundStatusOk(data: unknown): data is Record<string, unknown> {
  return isRecord(data) && data.status === 'OK';
}

/**
 * Formats a server amount for a bound block, or undefined when it cannot be shown.
 *
 * ## Invariants:
 * - Non-numeric, non-finite and negative amounts return undefined: the price line is omitted
 *   rather than shown as "$0.00" or "$NaN".
 * - A per-month amount with no cents renders whole dollars ("$145"); everything else renders two
 *   decimals through `formatCurrencyAmount` ("$3.00", "$1,234.56"). Cents are judged on the value
 *   rounded to the cent, so backend float noise (145.0000001) still reads as whole dollars.
 */
export function formatSitePrice(
  amount: unknown,
  options: { perMonth: boolean }
): string | undefined {
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) return undefined;
  const cents = Math.round(amount * 100);
  if (options.perMonth && cents % 100 === 0) {
    return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return formatCurrencyAmount(cents / 100);
}

function boundLog(route: string, reason: string): void {
  logger.warn('site-page: bound data value could not be formatted', {
    ...LOG_CONTEXT,
    route,
    reason,
  });
}

/**
 * A server location code as a URL path segment, or undefined when it cannot be encoded.
 *
 * ## Invariants:
 * - `encodeURIComponent` throws `URIError` on a lone UTF-16 surrogate. Such a code drops only its
 *   own location / event, logged without the code value, instead of throwing out of the mapper
 *   (which would hide the whole section). Every encodable code is returned exactly as
 *   `encodeURIComponent` returns it.
 */
function encodeLocationCode(code: string, route: string): string | undefined {
  try {
    return encodeURIComponent(code);
  } catch (error) {
    boundLog(route, error instanceof Error ? `location-code-${error.name}` : 'location-code');
    return undefined;
  }
}

/** "City, ST" from whichever parts are present; undefined when neither is. */
function placeLine(record: Record<string, unknown>): string | undefined {
  const parts = [stringSlot(record, 'city'), stringSlot(record, 'state')].filter(
    (part): part is string => part !== undefined
  );
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/** DIRECTORY: groups with at least one location that has a code; null when none survive. */
export function mapDirectoryView(
  slots: Record<string, unknown>,
  data: unknown
): DirectoryView | null {
  if (!boundStatusOk(data)) return null;
  const groups: DirectoryGroupView[] = [];
  for (const group of recordItems(data.groups)) {
    const key = stringSlot(group, 'key');
    if (!key) continue;
    const locations: DirectoryLocationView[] = [];
    for (const location of recordItems(group.locations)) {
      const name = stringSlot(location, 'name');
      const code = stringSlot(location, 'locationCode');
      if (!name || !code) continue;
      const segment = encodeLocationCode(code, 'directory');
      if (segment === undefined) continue;
      locations.push({ name, href: BOOKING_ROUTES.daily(segment) });
    }
    if (locations.length === 0) continue;
    const count =
      typeof group.count === 'number' && Number.isInteger(group.count) && group.count > 0
        ? group.count
        : locations.length;
    groups.push({
      key,
      label: key === '_other' ? undefined : stringSlot(group, 'label'),
      count,
      locations,
    });
  }
  return groups.length > 0 ? { heading: stringSlot(slots, 'heading'), groups } : null;
}

/** Amenity names this client has a label for, in server order; unknown names are skipped. */
function mapAmenities(value: unknown): FeaturedAmenityView[] {
  if (!Array.isArray(value)) return [];
  const amenities: FeaturedAmenityView[] = [];
  for (const code of value) {
    if (typeof code !== 'string') continue;
    if (!Object.prototype.hasOwnProperty.call(AMENITY_LABEL_KEYS, code)) continue;
    amenities.push({ code, labelKey: AMENITY_LABEL_KEYS[code] });
  }
  return amenities;
}

/**
 * FEATURED_LOCATIONS cards; null when no location with a code survives.
 *
 * ## Invariants:
 * - The price is the advertised MONTHLY rate (api `AdvertisedRateResolver`), so it reads
 *   "from $X/mo" and the card opens the monthly purchase page `/p/{code}` (PB-8614 decision 2a).
 * - `imageUrl` is re-checked as https here even though the api already filters it: the browser
 *   loads it directly.
 */
export function mapFeaturedLocationsView(
  slots: Record<string, unknown>,
  data: unknown
): FeaturedLocationsView | null {
  if (!boundStatusOk(data)) return null;
  const locations: FeaturedLocationView[] = [];
  for (const location of recordItems(data.locations)) {
    const name = stringSlot(location, 'name');
    const code = stringSlot(location, 'locationCode');
    if (!name || !code) continue;
    const segment = encodeLocationCode(code, 'featured-locations');
    if (segment === undefined) continue;
    const price = isRecord(location.fromPrice) ? location.fromPrice : undefined;
    const perMonth = price?.cadence === 'MONTHLY';
    locations.push({
      key: stringSlot(location, 'garageId') ?? code,
      name,
      place: placeLine(location),
      imageUrl: httpsImageSlot(location, 'imageUrl'),
      amenities: mapAmenities(location.amenities),
      fromPrice: price ? formatSitePrice(price.amount, { perMonth }) : undefined,
      perMonth,
      href: BOOKING_ROUTES.permit(segment),
    });
  }
  return locations.length > 0 ? { heading: stringSlot(slots, 'heading'), locations } : null;
}

/**
 * MONTHLY_PITCH; null without a usable monthly price.
 *
 * ## Invariants:
 * - Hidden on EMPTY even though the api contract allows a copy-only pitch: the Surface Spec and
 *   the AC agree that a bound block with no data is not rendered (PB-8614 deviation 3). The
 *   company monthly toggle is applied by `isBoundSectionShown`, not here.
 */
export function mapMonthlyPitchView(
  slots: Record<string, unknown>,
  data: unknown
): MonthlyPitchView | null {
  if (!boundStatusOk(data) || !isRecord(data.fromMonthly)) return null;
  const fromPrice = formatSitePrice(data.fromMonthly.amount, { perMonth: true });
  if (!fromPrice) return null;
  return {
    heading: stringSlot(slots, 'heading'),
    body: stringSlot(slots, 'body'),
    ctaLabel: stringSlot(slots, 'ctaLabel'),
    fromPrice,
  };
}

/** `Z` → UTC; a trailing `±hh:mm` → that offset; anything else → undefined (never the browser zone). */
function isoOffsetZone(iso: string): string | undefined {
  if (iso.endsWith('Z') || iso.endsWith('z')) return 'UTC';
  const tail = iso.slice(-6);
  return /^[+-]\d{2}:\d{2}$/.test(tail) ? tail : undefined;
}

/**
 * "Thu, Apr 23 · 7:00 PM" for an event start, in the company zone.
 *
 * ## Invariants:
 * - The zone is `data.timeZone`, else the offset written in the start string itself. It is never
 *   the viewer's browser zone: `formatInGarageTz` falls back to browser-local when given no zone,
 *   so this function returns undefined instead of calling it without one.
 */
function formatEventWhen(start: string, timeZone: string | undefined): string | undefined {
  const zone = timeZone ?? isoOffsetZone(start);
  if (!zone) {
    boundLog('events', 'no-zone');
    return undefined;
  }
  if (Number.isNaN(Date.parse(start))) {
    boundLog('events', 'start');
    return undefined;
  }
  try {
    return `${formatInGarageTz(start, 'EEE, MMM d', zone)} · ${formatInGarageTz(start, 'h:mm a', zone)}`;
  } catch (error) {
    boundLog('events', error instanceof Error ? error.name : 'format');
    return undefined;
  }
}

/** EVENTS rows (server-ordered, past events already removed); null when none has a bookable location. */
export function mapEventsView(slots: Record<string, unknown>, data: unknown): EventsView | null {
  if (!boundStatusOk(data)) return null;
  const timeZone = stringSlot(data, 'timeZone');
  const events: EventItemView[] = [];
  recordItems(data.events).forEach((event, index) => {
    const name = stringSlot(event, 'name');
    const location = recordItems(event.locations).find((candidate) =>
      stringSlot(candidate, 'locationCode')
    );
    const code = location ? stringSlot(location, 'locationCode') : undefined;
    if (!name || !location || !code) return;
    const segment = encodeLocationCode(code, 'events');
    if (segment === undefined) return;
    const start = stringSlot(event, 'start');
    const price = isRecord(event.fromPrice) ? event.fromPrice : undefined;
    events.push({
      key: `${index}-${name}`,
      name,
      dateText: start ? formatEventWhen(start, timeZone) : undefined,
      venue: stringSlot(location, 'name'),
      price: price ? formatSitePrice(price.amount, { perMonth: false }) : undefined,
      href: BOOKING_ROUTES.daily(segment),
    });
  });
  return events.length > 0 ? { heading: stringSlot(slots, 'heading'), events } : null;
}

const MAX_STATS = 4;

/**
 * STATS tiles, in the operator's slot order; null when no tile has a value.
 *
 * ## Invariants:
 * - Slot item `i` pairs with the data item whose `index` is `i`. MANUAL shows the operator's
 *   `manualValue` verbatim ("13.5K"); the other modes show the server's number with grouping. A
 *   tile with no value (YEARS_SINCE without a usable founded year, blank MANUAL) is dropped.
 */
export function mapStatsView(slots: Record<string, unknown>, data: unknown): StatsView | null {
  if (!boundStatusOk(data)) return null;
  const dataItems = recordItems(data.items);
  const items: StatItemView[] = [];
  recordItems(slots.items)
    .slice(0, MAX_STATS)
    .forEach((slotItem, index) => {
      const bound = dataItems.find((item) => item.index === index);
      if (!bound) return;
      const mode = bound.valueMode ?? slotItem.valueMode;
      let value: string | undefined;
      if (mode === 'MANUAL') {
        value = stringSlot(slotItem, 'manualValue');
      } else if (typeof bound.value === 'number' && Number.isFinite(bound.value)) {
        value = bound.value.toLocaleString('en-US');
      }
      if (value === undefined) return;
      items.push({ value, label: stringSlot(slotItem, 'label') });
    });
  return items.length > 0 ? { items } : null;
}

/**
 * The promotion's last day as "Apr 30, 2026", from the api's company-local `yyyy-MM-dd`.
 *
 * ## Invariants:
 * - A calendar date, not an instant: it is read at UTC noon and formatted in UTC, so no zone
 *   (server, company or viewer) can move it to a neighbouring day. A value that does not
 *   round-trip (e.g. `2026-02-31`) is dropped and logged.
 */
function formatPromotionEnd(endsOn: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endsOn)) {
    boundLog('promotion', 'ends-on-shape');
    return undefined;
  }
  const instant = `${endsOn}T12:00:00Z`;
  if (
    Number.isNaN(Date.parse(instant)) ||
    formatInGarageTz(instant, 'yyyy-MM-dd', 'UTC') !== endsOn
  ) {
    boundLog('promotion', 'ends-on-date');
    return undefined;
  }
  return formatInGarageTz(instant, 'MMM d, yyyy', 'UTC');
}

/**
 * PROMOTION card; null unless the server says the code is active.
 *
 * ## Invariants:
 * - Requires `status OK` AND `codeActive === true` AND a non-blank `code` slot: an inactive,
 *   expired or unknown code never renders (AC 4).
 * - The terms link goes through `resolveSiteLink` (https only for an external URL).
 */
export function mapPromotionView(
  slots: Record<string, unknown>,
  data: unknown
): PromotionView | null {
  if (!boundStatusOk(data) || data.codeActive !== true) return null;
  const code = stringSlot(slots, 'code');
  if (!code) return null;
  const endsOn = stringSlot(data, 'endsOn');
  return {
    headline: stringSlot(slots, 'headline'),
    code,
    terms: resolveSiteLink(slots.termsUrl),
    endsOn: endsOn ? formatPromotionEnd(endsOn) : undefined,
    ctaAction: slots.targetProduct === 'MONTHLY' ? 'MONTHLY' : 'BOOK',
  };
}

/**
 * Whether a section passes the bound-data gate.
 *
 * ## Invariants:
 * - Every non-bound type returns true: this gate never changes what the pre-PB-8614 blocks
 *   render.
 * - A bound type is shown only when its mapper returns a view, so missing / EMPTY / UNAVAILABLE
 *   data, or data with nothing usable, leaves no section wrapper (AC 5). MONTHLY_PITCH also
 *   needs the company's monthly toggle.
 * - Kept apart from `isSectionSupported` on purpose: the renderer's "unknown block type" warning
 *   reads only that predicate, and an empty bound section is a normal outcome, not an unknown
 *   type.
 * - A mapper that throws hides only that section (false) and is reported through `logger.error`
 *   with `{ type, sectionId }` only, never `data` or any code / location value. This runs in the
 *   renderer's `visibleSections` memo, OUTSIDE every `SectionBoundary`, so an uncaught throw here
 *   would take down the whole page instead of one section.
 */
export function isBoundSectionShown(
  section: SiteSectionView,
  options: { monthlyEnabled: boolean }
): boolean {
  const { slots, data } = section;
  try {
    switch (section.type) {
      case 'DIRECTORY':
        return mapDirectoryView(slots, data) !== null;
      case 'FEATURED_LOCATIONS':
        return mapFeaturedLocationsView(slots, data) !== null;
      case 'MONTHLY_PITCH':
        return options.monthlyEnabled && mapMonthlyPitchView(slots, data) !== null;
      case 'EVENTS':
        return mapEventsView(slots, data) !== null;
      case 'STATS':
        return mapStatsView(slots, data) !== null;
      case 'PROMOTION':
        return mapPromotionView(slots, data) !== null;
      default:
        return true;
    }
  } catch (error) {
    logger.error('site-page: bound mapper failed', error, {
      feature: 'site-page',
      route: 'render',
      type: section.type,
      sectionId: section.id,
    });
    return false;
  }
}
